import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { User, Calendar, Tag, Eye, Bookmark, Check } from "lucide-react";
import ReactionBar from "./ReactionBar";
import CommentSection from "./CommentSection";
import ShareButtons from "./ShareButtons";

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const userId = 1; // Replace with actual logged-in user ID
  const [scrollProgress, setScrollProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!slug) return;

    setLoading(true);
    setError(false);

    fetch(`https://100billionrealestate.com/api/blogs/slug/${slug}`)
      .then((res) => {
        if (!res.ok) throw new Error("Blog not found");
        return res.json();
      })
      .then((data) => {
        let desc = data.description;
        try {
          if (typeof desc === "string") {
            const parsed = JSON.parse(desc);
            desc = Array.isArray(parsed) ? parsed : [parsed];
          } else if (!Array.isArray(desc)) {
            desc = [desc];
          }
        } catch {
          desc = [desc];
        }

        desc = desc.filter((d) => typeof d === "string" && d.trim() !== "");
        setBlog({ ...data, description: desc });
        setLoading(false);
      })
      .catch((err) => {
        console.error("❌ Blog fetch failed:", err);
        setError(true);
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = (scrollTop / docHeight) * 100;
      setScrollProgress(scrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const checkSaved = async () => {
      try {
        const res = await fetch(`https://100billionrealestate.com/api/save/${userId}`);
        const data = await res.json();
        const found = data.find((item) => item.blogId === blog?._id);
        setIsSaved(!!found);
      } catch (err) {
        console.error("❌ Save check failed:", err);
      }
    };

    if (blog?._id) checkSaved();
  }, [blog?._id]);

  if (loading) return <p className="text-center text-gray-500 py-8">Loading blog...</p>;
  if (error) return <p className="text-center text-red-500 py-8">Blog not found.</p>;

  const formattedDate = new Date(blog.publishedAt || blog.createdAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <>
      {/* Scroll Progress Bar */}
      <div
        className="fixed top-[64px] left-0 h-1 bg-blue-600 z-50 transition-all duration-200"
        style={{ width: `${scrollProgress}%` }}
      />

      <div className="px-4 py-12 mt-8 max-w-[1100px] mx-auto">
        {blog.isFeatured && (
          <div className="inline-block bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-sm font-medium px-4 py-1 rounded-full mb-4">
            🔥 Featured
          </div>
        )}

        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{blog.title}</h1>

        {/* Meta Info */}
        <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-6">
          <span className="flex items-center gap-1">
            <User size={16} /> {blog.authorName || "Unknown Author"}
          </span>
          <span className="flex items-center gap-1">
            <Calendar size={16} /> {formattedDate}
          </span>
          <span className="flex items-center gap-1">
            <Tag size={16} /> {blog.category || "Uncategorized"}
          </span>
          <span className="flex items-center gap-1">
            <Eye size={16} /> {blog.views || 0} views
          </span>
        </div>

        {blog.image1 && (
          <img
            src={`https://100billionrealestate.com/${blog.image1}`}
            alt={blog.title}
            className="w-full h-64 object-cover rounded-lg shadow mb-4"
          />
        )}

        {/* CTA Button */}
        {blog.actionType && blog.actionValue && (
          <div className="my-4">
            <a
              href={
                blog.actionType === "call"
                  ? `tel:${blog.actionValue}`
                  : blog.actionValue
              }
              target={blog.actionType === "link" ? "_blank" : "_self"}
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-md font-medium shadow hover:scale-105 transition-transform"
            >
              <i className="fa-solid fa-arrow-right" /> Get Started Now
            </a>
          </div>
        )}

        {/* Blog Description */}
        <div className="prose max-w-none prose-blue prose-lg">
          {Array.isArray(blog.description)
            ? blog.description.map((para, idx) => <p key={idx}>{para}</p>)
            : <p>{blog.description}</p>}
        </div>

        {blog.image2 && (
          <img
            src={`https://100billionrealestate.com/${blog.image2}`}
            alt="Secondary Visual"
            className="w-full h-64 object-cover rounded-lg shadow mt-10 mb-8"
          />
        )}

        {/* Save Button (Optional)
        <button
          onClick={handleSave}
          disabled={isSaved}
          className="mt-6 flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition"
        >
          {isSaved ? <><Check size={18} /> Saved</> : <><Bookmark size={18} /> Save for Later</>}
        </button> */}


        <ReactionBar blogId={blog?.slug} />
        <CommentSection slug={blog.slug} />
        <ShareButtons blogUrl={window.location.href} blogTitle={blog.title} />

      </div>
    </>
  );
};

export default BlogDetail;
